import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle,
  Error as ErrorIcon,
  CloudUpload as UploadIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  QrCode2 as QrCodeIcon,
} from '@mui/icons-material';
import { apiService } from '@signals/app-shell/services/api';
import MqttSetupQrDialog from '../shared/MqttSetupQrDialog';
import InfoHint from '@signals/app-shell/components/common/InfoHint';

interface MQTTConfig {
  broker_host: string;
  broker_port: number;
  username: string;
  password_set: boolean;
  use_tls: boolean;
  ca_cert_filename: string | null;
  enabled: boolean;
  connected: boolean;
  messages_received: number;
  messages_processed: number;
}

const MQTTConnectionSettings: React.FC = () => {
  const [config, setConfig] = useState<MQTTConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);

  const [brokerHost, setBrokerHost] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [certFile, setCertFile] = useState<File | null>(null);

  const MQTTS_PORT = 8883;

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await apiService.client.get('/mqtt/config');
      const data: MQTTConfig = resp.data;
      setConfig(data);
      setBrokerHost(data.broker_host);
      setUsername(data.username);
      setPassword('');
      setEnabled(data.enabled);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load MQTT configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const formData = new FormData();
      formData.append('broker_host', brokerHost);
      formData.append('broker_port', String(MQTTS_PORT));
      formData.append('username', username);
      if (password) {
        formData.append('password', password);
      } else if (config?.password_set) {
        formData.append('password', '__KEEP__');
      }
      formData.append('use_tls', 'true');
      formData.append('enabled', String(enabled));
      if (certFile) {
        formData.append('ca_cert', certFile);
      }

      const resp = await apiService.client.post('/mqtt/config', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setConfig(resp.data);
      setCertFile(null);
      setPassword('');
      setSuccess(
        resp.data.connected
          ? 'Configuration saved — MQTT connected successfully.'
          : 'Configuration saved. MQTT is not connected — check broker address and credentials.',
      );
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to save MQTT configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          MQTT Broker Connection
        </Typography>
        <Tooltip title="Refresh status">
          <IconButton onClick={fetchConfig} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {config && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Chip
              icon={config.connected ? <CheckCircle /> : <ErrorIcon />}
              label={config.connected ? 'Connected' : 'Disconnected'}
              color={config.connected ? 'success' : 'error'}
              variant="outlined"
              size="small"
            />
            {config.enabled && (
              <>
                <Typography variant="body2" color="text.secondary">
                  {config.broker_host}:{MQTTS_PORT}
                </Typography>
                <Chip icon={<LockIcon />} label="MQTTS" size="small" variant="outlined" color="info" />
                {config.ca_cert_filename && (
                  <Typography variant="body2" color="text.secondary">
                    Cert: {config.ca_cert_filename}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                  Msgs: {config.messages_received.toLocaleString()} received / {config.messages_processed.toLocaleString()} processed
                </Typography>
              </>
            )}
            {!config.enabled && (
              <Typography variant="body2" color="text.secondary">MQTT is disabled on this web process</Typography>
            )}
            <InfoHint title="Where ingest actually runs">
              <p>
                This page talks to the App Service. Live phone and Network Survey ingest attach to
                the worker VM, which is kept separate on purpose.
              </p>
              <p>
                Save host, username, and password here so QR provisioning works. Enable / Save
                &amp; Reconnect only starts a subscriber in this web process — it does not turn
                on worker ingest. Disconnected here is often expected.
              </p>
            </InfoHint>
          </CardContent>
        </Card>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
                label="Enable MQTT Connection"
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Broker Host"
                value={brokerHost}
                onChange={(e) => setBrokerHost(e.target.value)}
                placeholder="pf-demo-mqtts.eastus.cloudapp.azure.com"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Port"
                value={MQTTS_PORT}
                size="small"
                disabled
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <InfoHint title="Port">MQTTS only — 8883, not editable.</InfoHint>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={config?.password_set ? '••••••••  (unchanged)' : ''}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                  size="small"
                >
                  {certFile ? certFile.name : 'Upload CA Certificate (optional)'}
                  <input
                    type="file"
                    hidden
                    accept=".crt,.pem,.cer"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setCertFile(e.target.files[0]);
                      }
                    }}
                  />
                </Button>
                {!certFile && config?.ca_cert_filename && (
                  <Typography variant="body2" color="text.secondary">
                    Current: {config.ca_cert_filename}
                  </Typography>
                )}
                {certFile && (
                  <Button size="small" color="secondary" onClick={() => setCertFile(null)}>
                    Clear
                  </Button>
                )}
                <InfoHint title="CA certificate">
                  Optional for publicly trusted broker certificates. Upload only for private or
                  self-signed CAs.
                </InfoHint>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || !brokerHost}
                  startIcon={saving ? <CircularProgress size={16} /> : undefined}
                >
                  {saving ? 'Saving & Reconnecting...' : 'Save & Reconnect'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setError(null);
                    setSuccess(null);
                    setQrDialogOpen(true);
                  }}
                  startIcon={<QrCodeIcon />}
                  disabled={!brokerHost && !config?.broker_host}
                >
                  Show MQTT QR
                </Button>
                <InfoHint title="MQTT QR">
                  Generate a QR for Pathfinder Monitor or Network Survey. Save MQTT settings (with
                  password) first.
                </InfoHint>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <MqttSetupQrDialog
        open={qrDialogOpen}
        onClose={() => setQrDialogOpen(false)}
        onError={(message) => setError(message)}
      />
    </Box>
  );
};

export default MQTTConnectionSettings;
